import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { Header, Card, Loader, Button, useDrawer, Grid } from "../../../design";
import {
    useAxios,
    handleError,
    setWindowVariables,
    transformMultilingualDataForLineItems,
    convertToCamelCase,
    useWindowDimensions,
    kebabize,
    capitalizeFirstLetter,
} from "../../../utils/helpers";
import { replacePlaceholders, translate, translateLocalizedEntity } from "../../../utils/translations";
import { useLoop } from "../../../components/loop-provider";
import LogoutModal from "../../../components/modals/logout";
import SubscriptionCard from "../../../components/cards/subscription";
import NewSubscriptionCard from "../../../components/cards/new-subscription";
import QuickAction from "../../../components/quick-action";
import CreateSubscriptionDrawer from "../../../components/drawers/create-subscription";
import MergeSubscriptionsDrawer from "../../../components/drawers/merge-subscriptions";
import { useLoyalty } from "../../../components/loyalty-provider";
import CreateSubscriptionDrawerV2 from "../../../components/drawers/create-subscription-drawer-v2";
import { useLoopAuth } from "../../../components/auth-provider";
import GiftSubscriptionCard from "../../../components/gift/cards/gift-subscription";
import { LoyaltyPointsRedeemEntity } from "../../../utils/constants";
import FourOhFour from "../../404";
import { APIFunction } from "../../../utils/apiFunction";
import LoyaltyOverviewDrawerFriendbuy from "../../../components/loyalty/loyalty-fb-overview";
import { GetServerSidePropsContext } from "next";
import { getServerSideAuth } from "../../../backend/auth";
import { getServerSideTheme, getServerSideStoreData } from "../../../backend/theme";
import { CustomerPortalContext, SubscriptionsServerData } from "../../../backend/serverProps";
import { handleRedirectResult } from "../../../backend/utils";
import { ServerAPIFunction } from "../../../backend/serverAPI";

const NUMBER_OF_COLS = 2;
const GRID_BREAKPOINT = 576;

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const [authResult, themeResult] = await Promise.all([
        getServerSideAuth(context),
        getServerSideTheme(context)
    ]);

    const authRedirect = handleRedirectResult(authResult);
    if (authRedirect) {
        return authRedirect;
    }

    const themeRedirect = handleRedirectResult(themeResult);
    if (themeRedirect) {
        return themeRedirect;
    }

    // Get store data using the theme nano ID
    const storeDataResult = await getServerSideStoreData(
        context,
        'themeNanoId' in themeResult && typeof themeResult?.themeNanoId === 'string' ? themeResult.themeNanoId : undefined,
        'loopAccessToken' in authResult && typeof authResult?.loopAccessToken === 'string' ? authResult.loopAccessToken : ''
    );

    // Get customer subscriptions data for the main customer page
    let subscriptionsListData = null;
    if ('loopAccessToken' in authResult && authResult.loopAccessToken && 'shopifyDomain' in authResult && authResult.shopifyDomain) {
        try {
            subscriptionsListData = await ServerAPIFunction.getAllCustomerSubscriptions(
                authResult.customerShopifyId,
                authResult.loopAccessToken,
                authResult.shopifyDomain
            );
        } catch (error) {
            console.error('Failed to fetch customer subscriptions on server:', error);
            // Continue without subscriptions data, let client handle the error
        }
    }

    return {
        props: {
            ...authResult,
            ...themeResult,
            ...storeDataResult,
            subscriptionsListData,
        }
    }
};

export const SubscriptionsListActions = ({
    mergeGroups,
    handleMultipassLink,
    toggleMergeSubscriptionDrawer,
    showAddSubscriptionButton,
    toggleCreateSubscriptionDrawer,
    loyaltyButtonFriendbuy,
}) => {
    const { preferences, integrations } = useLoop();

    function isMergeEligible() {
        let mergeEligibleCount = 0;
        mergeGroups.forEach(group => {
            const nonDigitalContracts = group.contracts.filter(c => !c.isDigitalSubscription);
            if (nonDigitalContracts.length < 2) {
                return;
            }

            const basePolicy = nonDigitalContracts[0].billing_policy

            const eligibleContracts = nonDigitalContracts.filter(c =>
                c.billing_policy.max_cycles === basePolicy.max_cycles &&
                c.billing_policy.min_cycles === basePolicy.min_cycles
            );
            mergeEligibleCount += eligibleContracts.length;
        });
        if (mergeEligibleCount >= 2) {
            console.log("Displaying merge subscription button");
            return true;
        } else {
            console.log("Hiding merge subscription button. No eligible subscription found for merging.");
            return false;
        }
    }

    return (
        <div className="loop-cp-subscription-list-actions-container d-flex flex-column flex-md-row justify-content-between align-items-start">
            <div className="loop-cp-subscription-list-actions-left d-flex flex-column align-items-start">
                <Header.H2>{translate("subscription_listing_active_subscriptions_header")}</Header.H2>
                {integrations?.multipass && (
                    <Button
                        variant="underline"
                        onClick={() => handleMultipassLink(integrations?.multipass?.data?.redirectTo)}
                        id="loop-listing-multipass-btn"
                    >
                        {translate("subscription_listing_multipass_button")}
                    </Button>
                )}
                {preferences?.cp_merge_enabled && mergeGroups.length > 0 && isMergeEligible() ? (
                    <Button variant="underline" onClick={toggleMergeSubscriptionDrawer} id="loop-listing-merge-btn">
                        {translate("subscription_listing_merge_button")}
                    </Button>
                ) : null}
            </div>
            <div className="loop-cp-subscription-list-actions-right d-flex flex-column">
                {preferences.cp_subscription_create === 1 && showAddSubscriptionButton() && (
                    <Button className="mt-md-0 mt-2" size="lg" onClick={toggleCreateSubscriptionDrawer}>
                        {translate("subscription_listing_create_button")}
                    </Button>
                )}
                {loyaltyButtonFriendbuy}
            </div>
        </div>
    );
};

export const ActiveContractsContainer = ({
    activeContracts,
    newContracts,
    loadContracts,
    customer_shopify_id,
    showAddSubscriptionButton,
    toggleCreateSubscriptionDrawer,
    storeDefaultLocale,
    contractsInitiallyLoaded,
}) => {
    const { preferences } = useLoop();

    if (activeContracts.length > 0 || newContracts.length > 0) {
        return (
            <div className="loop-cp-subscription-list-active-contracts-container">
                {newContracts.map((contract, i) => (
                    <NewSubscriptionCard key={i} contract={contract} reloadContracts={loadContracts} />
                ))}
                {activeContracts.map((contract, i) => (
                    <React.Fragment key={i}>
                        {contract?.isGift ? (
                            <GiftSubscriptionCard
                                key={i}
                                contract={contract}
                                customer_shopify_id={customer_shopify_id}
                            />
                        ) : (
                            <SubscriptionCard
                                key={i}
                                contract={contract}
                                customer_shopify_id={customer_shopify_id}
                                storeDefaultLocale={storeDefaultLocale}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    // Don't show "no contracts" message until contracts have been initially loaded
    if (!contractsInitiallyLoaded) {
        return <Loader />;
    }

    return (
        <Card body={true} className="my-4">
            <div className="loop-cp-subscription-list-no-contracts-container d-flex flex-column justify-content-center align-items-center m-4 p-4">
                <Header.H2>{translate("subscription_listing_no_active_subscriptions_text")}</Header.H2>
                <br />
                {preferences.cp_subscription_create === 1 && showAddSubscriptionButton() && (
                    <Button size="lg" onClick={toggleCreateSubscriptionDrawer}>
                        {translate("subscription_listing_create_button")}
                    </Button>
                )}
            </div>
        </Card>
    );
};

export const InactiveContractsContainer = ({ inactiveContracts, customer_shopify_id, storeDefaultLocale }) => {
    if (!inactiveContracts.length) return null;

    return (
        <>
            <div className="loop-cp-subscription-list-inactive-contracts-header d-flex justify-content-between align-items-center">
                <Header.H2>{translate("subscription_listing_inactive_subscriptions_header")}</Header.H2>
            </div>
            <div className="loop-cp-subscription-list-inactive-contracts-container">
                {inactiveContracts.map((contract, i) => (
                    <React.Fragment key={i}>
                        {contract?.isGift ? (
                            <GiftSubscriptionCard
                                key={i}
                                contract={contract}
                                customer_shopify_id={customer_shopify_id}
                            />
                        ) : (
                            <SubscriptionCard
                                key={i}
                                contract={contract}
                                customer_shopify_id={customer_shopify_id}
                                storeDefaultLocale={storeDefaultLocale}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

export const LogoutButtonSubscriptions = ({ setLogoutModalOpen }) => {
    return (
        <div className="loop-cp-subscription-list-logout-button-container cp-logout-button" id="loop-logout-button">
            <Button variant="underline" onClick={() => setLogoutModalOpen(true)}>
                {translate("cp_logout_button")}
            </Button>
        </div>
    );
};

export default function CustomerListView(customerPortalContext: CustomerPortalContext) {

    const router = useRouter();
    const [axios] = useAxios();
    const { generateMultipassLink, myshopify_domain, locale, localeRoute, themeNanoId, store } = useLoop();
    const { influenceData, friendbuyData, yotpoData, rivoData, bubblehouseData } = useLoyalty();
    const { width } = useWindowDimensions();
    const { customer, storeId } = useLoopAuth();
    const drawers = useDrawer();

    const [isLoading, setIsLoading] = useState(true);
    const [contractsInitiallyLoaded, setContractsInitiallyLoaded] = useState(false);

    const [mergeGroups, setMergeGroups] = useState([]);
    const [newContracts, setNewContracts] = useState([]);
    const [activeContracts, setActiveContracts] = useState([]);
    const [inactiveContracts, setInactiveContracts] = useState([]);
    const [customerPaymentMethodsCount, setcustomerPaymentMethodsCount] = useState(0);
    const [storeDefaultLocale, setStoreDefaultLocale] = useState(null);

    const [components, setComponents] = useState([]);
    const [calculatedComponents, setCalculatedComponents] = useState([]);
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);

    const findMergeAbleSubscriptions = (_activeContracts: Array<any> | undefined) => {
        let _hash = {};

        if (!_activeContracts) {
            setMergeGroups([]);
            return;
        }

        for (let contract of _activeContracts) {
            if (contract.isPrepaidV2) {
                continue;
            }
            let _key = "";
            _key += contract.billing_policy
                ? "" +
                contract.billing_policy.interval +
                "" +
                contract.billing_policy.interval_count +
                "" +
                contract.billing_policy.max_cycles +
                "" +
                contract.billing_policy.anchor_type +
                "" +
                contract.billing_policy.anchor_day +
                "" +
                contract.billing_policy.anchor_month
                : "";
            _key += contract.delivery_policy
                ? "" +
                contract.delivery_policy.interval +
                "" +
                contract.delivery_policy.interval_count
                : "";
            _key += contract.currency_code;
            if (contract.shipping_address) {
                _key +=
                    String(contract?.shipping_address?.zip || "").trim() +
                    String(contract?.shipping_address?.province_code || "").trim() +
                    String(contract?.shipping_address?.country_code || "").trim();
            }
            if (!(_key in _hash)) {
                _hash[_key] = {
                    billing_policy: { ...contract.billing_policy },
                    delivery_policy: { ...contract.delivery_policy },
                    shipping_address: { ...contract.shipping_address },
                    contracts: [],
                };
            }
            _hash[_key].contracts.push(contract);
        }
        let ret: Array<any> = [];
        for (let key in _hash) {
            if (_hash[key].contracts.length > 1) {
                ret.push(_hash[key]);
            }
        }
        setMergeGroups(ret);
    };

    const loadContracts = async () => {
        setIsLoading(true);
        try {
            let dataNew, dataActive, dataInactive, dataStore, dataCustomerPaymentMethodsCount;

            // Use server-side data if available, otherwise fetch from client for fallback
            if (customerPortalContext.subscriptionsListData && !contractsInitiallyLoaded) {
                const serverData = customerPortalContext.subscriptionsListData;
                dataNew = serverData.new;
                dataActive = serverData.active;
                dataInactive = serverData.other;
                dataStore = serverData.store;
                dataCustomerPaymentMethodsCount = serverData.paymentMethodsCount;
            } else {
                const _params: any = {
                    shopify_customer_id: router.query.customer_shopify_id + "",
                };
                const [
                    { data: clientDataNew },
                    { data: clientDataActive },
                    { data: clientDataInactive },
                    { data: clientDataStore },
                    { data: clientDataCustomerPaymentMethodsCount },
                ]: Array<any> = await Promise.all([
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/new?` +
                        new URLSearchParams(_params),
                    ),
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/active?` +
                        new URLSearchParams(_params),
                    ),
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/other?` +
                        new URLSearchParams(_params),
                    ),
                    axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/store_details`),
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/getCustomerPaymentMethodCount?` +
                        new URLSearchParams(_params),
                    ),
                ]);
                dataNew = clientDataNew;
                dataActive = clientDataActive;
                dataInactive = clientDataInactive;
                dataStore = clientDataStore;
                dataCustomerPaymentMethodsCount = clientDataCustomerPaymentMethodsCount;
            }

            if (locale !== dataStore?.data?.default_locale && dataStore?.data?.default_locale != null) {
                await transformMultilingualDataForLineItems(
                    dataNew?.data?.results ?? [],
                    myshopify_domain,
                    locale,
                    dataStore.data.default_locale,
                    localeRoute,
                );
                await transformMultilingualDataForLineItems(
                    dataActive?.data?.results ?? [],
                    myshopify_domain,
                    locale,
                    dataStore.data.default_locale,
                    localeRoute,
                );
                await transformMultilingualDataForLineItems(
                    dataInactive?.data?.results ?? [],
                    myshopify_domain,
                    locale,
                    dataStore.data.default_locale,
                    localeRoute,
                );
            }

            setStoreDefaultLocale(dataStore?.data?.default_locale);
            setNewContracts(dataNew?.data?.results ?? []);
            setActiveContracts(dataActive?.data?.results ?? []);
            setInactiveContracts(dataInactive?.data?.results ?? []);
            findMergeAbleSubscriptions(dataActive?.data?.results ?? []);
            setcustomerPaymentMethodsCount(dataCustomerPaymentMethodsCount?.data?.customerPaymentMethodsCount);
            setContractsInitiallyLoaded(true);
            const allSubscriptions = {
                activeSubscriptions: convertToCamelCase(dataActive?.data?.results ?? []),
                inactiveSubscriptions: convertToCamelCase(dataInactive?.data?.results ?? []),
            };
            setWindowVariables({ subscription: null, allSubscriptions });
        } catch (error) {
            handleError(error, "pages/customer/[customer_shopify_id]", "loadContracts", {});
        }
        setIsLoading(false);
    };

    const toggleCreateSubscriptionDrawer = () => {
        drawers.open({
            component: activeContracts.length ? CreateSubscriptionDrawerV2 : CreateSubscriptionDrawer,
            props: {
                reloadContracts: loadContracts,
                activeContracts: activeContracts,
            },
        });
    };

    const toggleMergeSubscriptionDrawer = () => {
        drawers.open({
            component: MergeSubscriptionsDrawer,
            props: {
                availableGroups: mergeGroups,
                reloadContracts: loadContracts,
            },
        });
    };

    const showAddSubscriptionButton = () => {
        return activeContracts.length + inactiveContracts.length > 0 && customerPaymentMethodsCount > 0;
    };

    const handleMultipassLink = async (link: string) => {
        setIsLoading(true);
        try {
            const data: any = await generateMultipassLink(link);
            if (data?.success) {
                window.location.href = `/account/login/multipass/${data.data.token}`;
            } else {
                // toasts.open({ message: translate(data.messageKey, data.placeholders), type: "failure", time: 10000 });
            }
        } catch (error) {
            // toasts.open({ message: translate('error_message'), type: "failure", time: 10000 });
        }
        setIsLoading(false);
    };

    const toggleLoyaltyDrawerFriendbuy = () => {
        drawers.open({
            component: LoyaltyOverviewDrawerFriendbuy,
            props: {
                entity: LoyaltyPointsRedeemEntity.LOYALTY_WIDGET
            },
        });
    };

    const loyaltyButtonFriendbuy = friendbuyData.loyaltyProvider ? (
        <Button variant="outline" id="loop-loyalty-btn" className="mt-1" onClick={toggleLoyaltyDrawerFriendbuy}>
            {translateLocalizedEntity(
                "CP_THEME_ADVENT_LOYALTY_FB",
                storeId,
                "loyalty_widget_btn_fb",
                translate("loyalty_widget_btn_fb"),
                {
                    points: friendbuyData.points,
                    coupons: friendbuyData.couponCount,
                },
            )}
        </Button>
    ) : null;

    function getDefaultComponents() {
        return [
            {
                componentType: "STANDARD",
                componentKey: "cpSubscriptionListActionsAndActiveContracts",
                width: 2,
            },
            {
                componentType: "STANDARD",
                componentKey: "cpSubscriptionListInactiveContracts",
                width: 2,
            },
        ];
    }

    const checkIfCpSubscriptionActionsPresentAndStopLoading = () => {
        let intervalId = setInterval(() => {
            let loopCpActionsBody = document.querySelector(".loop-cp-subscription-lists-actions");
            if (loopCpActionsBody) {
                clearInterval(intervalId);
                setIsLoading(false);
            }
        }, 200);
        setTimeout(() => {
            setIsLoading(false);
        }, 5000);
    };

    const loadLayout = async () => {
        setIsLoading(true);
        try {
            const res = await APIFunction.getThemeComponentsForPage(
                themeNanoId,
                myshopify_domain,
                "cpPageSubscriptionLists",
                locale,
            );

            if (res.data?.length) {
                setComponents(res.data);
            } else {
                setComponents(getDefaultComponents());
            }
        } catch (error) {
            setComponents(getDefaultComponents());
            checkIfCpSubscriptionActionsPresentAndStopLoading();
        }
        setIsLoading(false);
    };

    const getComponentWidth = (compWidth: number) => {
        if (width < GRID_BREAKPOINT) {
            return 1;
        }
        return Math.min(compWidth || NUMBER_OF_COLS, NUMBER_OF_COLS);
    };

    const recalculateGrid = () => {
        try {
            let _totalRows = 0;
            let _totalWidth = 0;
            let _customComponents = [];
            let _NUMBER_OF_COLS = width >= GRID_BREAKPOINT ? NUMBER_OF_COLS : 1;
            for (let _comp of components) {
                let _compWidth = getComponentWidth(_comp.width || _comp.defaultWidth);
                if (_totalWidth + _compWidth > _NUMBER_OF_COLS) {
                    _totalRows += 1;
                    _totalWidth = _compWidth;
                } else {
                    _totalWidth += _compWidth;
                }
                _customComponents.push({
                    type: _comp.componentType,
                    key: _comp.componentKey || "custom_component",
                    row: _totalRows + 1,
                    width: `${_totalWidth - _compWidth + 1} / ${_totalWidth + 1}`,
                    body: _comp.body,
                });
            }
            if (_totalWidth > 0) {
                _totalRows += 1;
            }
            setCalculatedComponents(_customComponents);
            checkIfCpSubscriptionActionsPresentAndStopLoading();
        } catch (error) {
            checkIfCpSubscriptionActionsPresentAndStopLoading();
        }
    };

    function getAdditionalClassesForGridItems(key: string) {
        if (!key) {
            return "";
        }

        const _gridKeyClasses = {
            cpSubscriptionListActionsAndActiveContracts: "",
            cpSubscriptionListInactiveContracts: "",
        };

        return _gridKeyClasses[key] || "";
    }

    const _gridKeyContent = {
        cpSubscriptionListActionsAndActiveContracts: (
            <>
                <SubscriptionsListActions
                    showAddSubscriptionButton={showAddSubscriptionButton}
                    handleMultipassLink={handleMultipassLink}
                    mergeGroups={mergeGroups}
                    toggleMergeSubscriptionDrawer={toggleMergeSubscriptionDrawer}
                    toggleCreateSubscriptionDrawer={toggleCreateSubscriptionDrawer}
                    loyaltyButtonFriendbuy={loyaltyButtonFriendbuy}
                />
                <ActiveContractsContainer
                    activeContracts={activeContracts}
                    newContracts={newContracts}
                    loadContracts={loadContracts}
                    customer_shopify_id={router.query.customer_shopify_id}
                    showAddSubscriptionButton={showAddSubscriptionButton}
                    toggleCreateSubscriptionDrawer={toggleCreateSubscriptionDrawer}
                    storeDefaultLocale={storeDefaultLocale}
                    contractsInitiallyLoaded={contractsInitiallyLoaded}
                />
            </>
        ),
        cpSubscriptionListInactiveContracts: (
            <InactiveContractsContainer
                inactiveContracts={inactiveContracts}
                customer_shopify_id={router.query.customer_shopify_id}
                storeDefaultLocale={storeDefaultLocale}
            />
        ),
        cpSubscriptionListLogoutButton: <LogoutButtonSubscriptions setLogoutModalOpen={setLogoutModalOpen} />,
    };

    useEffect(() => {
        if (router.isReady) {
            if (router.query.customer_shopify_id) {
                loadContracts();
            }
        }
    }, [router.isReady, router.query.customer_shopify_id]);

    useEffect(() => {
        if (themeNanoId) {
            loadLayout();
        }
    }, [themeNanoId]);

    useEffect(() => {
        if (components.length && width) {
            recalculateGrid();
        }
    }, [components, width]);

    if (isLoading) {
        return (
            <div className="loop-background" id="loop-background">
                <Loader />
            </div>
        );
    }

    return (
        <Grid className="px-3 py-4 loop-customer-portal-body">
            {calculatedComponents.map((component, i) => {
                return (
                    <Grid.Item2
                        key={i}
                        row={component.row}
                        width={component.width}
                        id={
                            component.type == "CUSTOM"
                                ? component.key
                                : kebabize(
                                    `loop${capitalizeFirstLetter(component.key)}`,
                                )
                        }
                        className={getAdditionalClassesForGridItems(component.key)}
                    >
                        {component.type == "STANDARD" ? (
                            _gridKeyContent[component.key]
                        ) : (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: replacePlaceholders(component.body, {
                                        customer_first_name: customer?.first_name,
                                        customer_last_name: customer?.last_name,
                                        customer_email: customer?.email,
                                        total_orders_placed_by_customer: customer?.total_orders_placed_by_customer,
                                        total_spent_by_customer: customer?.total_spent_by_customer,
                                        total_subscriptions_orders_by_customer: customer?.total_subscriptions_orders_by_customer,
                                        store_url: store?.store_url,
                                        store_name: store?.store_name,
                                        store_address: store?.store_address,
                                        store_support_email: store?.store_support_email,
                                        bubblehouse_points: bubblehouseData.points,
                                        bubblehouse_coupons: bubblehouseData.couponCount,
                                        bubblehouse_link: bubblehouseData.link,
                                    }),
                                }}
                            ></div>
                        )}
                    </Grid.Item2>
                );
            })}
            <QuickAction />
            <LogoutModal active={logoutModalOpen} toggleActive={() => setLogoutModalOpen(false)} />
        </Grid>
    );
}